import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, BUTTON_TYPES, Input, notification, NOTIFICATION_TYPES, Popup, RangePicker, Table } from '_atoms';
import { TableMobile } from '_atoms/Table/Table';
import { PopupCandidateDuplicate, PopupMarkAsReviewed, PopupReassignCandidate, TableFooter } from '_molecules';
import {
  bulkDeleteSSCAPI,
  deleteCandidate,
  getListHrManagers,
  getRecruiterSSCTableDashboardAPI,
  getSSCPotentialDuplicatesAPI,
} from 'actions';
import { ROLES } from 'permission';
import { getUser } from 'store/selectors/authorizeSelectors';
import type { DashboardSSCRow } from 'actions/dashboardActions';
import type { DuplicateCandidate } from 'store/reducers/candidateReducer';
import { DeleteCandidatePopup } from '../CandidateDetails/CandidateDetails';
import { Empty } from './Empty';
import { INITIAL_VALUES_SSC, COLUMNS_SSC } from './constants';
import './style.scss';

type SSCPopupRow = DashboardSSCRow & { first_name_latin?: string; last_name_latin?: string };

interface SelfSubmittedCandidatesDashboardProps {
  dashboard: string;
}

export const SelfSubmittedCandidatesDashboard: React.FC<SelfSubmittedCandidatesDashboardProps> = ({ dashboard }) => {
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<DashboardSSCRow[]>([]);
  const [tableOptions, setTableOptions] = useState(INITIAL_VALUES_SSC);
  const [count, setCount] = useState(0);

  const [assignPopup, setAssignPopup] = useState<SSCPopupRow | null>(null);
  const [reviewPopup, setReviewPopup] = useState<SSCPopupRow | null>(null);
  const [deletePopup, setDeletePopup] = useState<SSCPopupRow | null>(null);
  const [potentialDuplicates, setPotentialDuplicates] = useState<DuplicateCandidate[]>([]);

  const [openConfirmPopup, setOpenConfirmPopup] = useState(false);
  const [selectedRows, setSelectedRows] = useState<DashboardSSCRow[]>([]);

  const [duplicatesPopup, setDuplicatesPopup] = useState<SSCPopupRow | null>(null);

  const onAssignPopupClose = () => setAssignPopup(null);

  const onReviewPopupClose = () => setReviewPopup(null);
  const onDeletePopupClose = () => setDeletePopup(null);
  const onDuplicatesPopupClose = () => {
    setDuplicatesPopup(null);
    setPotentialDuplicates([]);
  };

  const getDuplicates = (data: SSCPopupRow, showMessage?: boolean) => {
    getSSCPotentialDuplicatesAPI(data.uuid).then(duplicates => {
      setPotentialDuplicates(duplicates || []);
      if (showMessage && duplicates?.length) {
        notification({
          message: t('translation:ssc_dashboard.text_found_duplicates', {
            count: duplicates.length,
          } as Record<string, unknown>),
          btn: (
            <span role="none" onClick={() => setDuplicatesPopup(data)}>
              {t('translation:ssc_dashboard.button_open_duplicates')}
            </span>
          ),
          type: NOTIFICATION_TYPES.WARNING,
        });
      }
    });
  };

  const onOpenConfirmPopup = () => {
    setOpenConfirmPopup(true);
  };
  const onCloseConfirmPopup = () => {
    setOpenConfirmPopup(false);
  };

  const getTableData = () => {
    setLoading(true);
    getRecruiterSSCTableDashboardAPI(dashboard, {
      limit: tableOptions.limit.value,
      offset: tableOptions.offset,
      sorting_field: tableOptions.sorting.field,
      sorting: tableOptions.sorting.order,
      search: tableOptions.search,
      creation_date_after: tableOptions.period?.[0]?.format('YYYY-MM-DD'),
      creation_date_before: tableOptions.period?.[1]?.format('YYYY-MM-DD'),
    })
      .then(d => {
        setTableData(d?.results || []);
        setTableOptions(o => ({ ...o, itemsCount: d?.count || 0 }));
        setCount(d?.count || 0);
      })
      .catch(() => {
        setTableData([]);
        setCount(0);
        setTableOptions(o => ({ ...o, itemsCount: 1 }));
        setTableOptions(INITIAL_VALUES_SSC);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onBulkDelete = () => {
    bulkDeleteSSCAPI(
      dashboard,
      selectedRows.map(r => r.candidate_id)
    )
      .then(() => {
        notification({
          type: NOTIFICATION_TYPES.SUCCESS,
          message: t('translation:ssc_dashboard.text_candidates_deleted'),
        });
        getTableData();
      })
      .finally(() => {
        onCloseConfirmPopup();
      });
  };

  const onAction = (action: string, data: SSCPopupRow) => () => {
    switch (action) {
      case 'edit':
        // eslint-disable-next-line no-case-declarations
        const link = document.createElement('a');
        link.href = `/self-submitted-candidates/${data.uuid}/edit`;
        link.target = '_blank';
        link.click();
        break;
      case 'assign':
        getDuplicates(data, true);
        setAssignPopup(data);
        break;
      case 'review':
        getDuplicates(data, true);
        setReviewPopup(data);
        break;
      case 'share':
        if (navigator.clipboard) {
          navigator.clipboard
            .writeText(`${window.location.origin}/self-submitted-candidates/preview/${data.candidate_id}`)
            .then(() => notification({ message: 'Link to profile has been copied', type: NOTIFICATION_TYPES.SUCCESS }));
        }
        break;
      case 'delete':
        setDeletePopup(data);
        break;
      case 'duplicates':
        getDuplicates(data);
        setDuplicatesPopup({ ...data, first_name_latin: data.full_name_latin, last_name_latin: data.full_name_latin });
    }
  };

  const onChangeFilter = (field, item) => {
    if (field === 'page') {
      setTableOptions(s => ({ ...s, page: item, offset: (item - 1) * s.limit.value }));
    } else if (['limit', 'search', 'period'].includes(field)) {
      setTableOptions(s => ({ ...s, [field]: item, offset: 0, page: 1 }));
    } else {
      setTableOptions(s => ({ ...s, [field]: item }));
    }
  };
  const setSorting = sort => onChangeFilter('sorting', sort);
  const setSearch = e => onChangeFilter('search', e.target.value);
  const setPeriod = date => onChangeFilter('period', date);

  const onAssign = () => {
    getTableData();
    setAssignPopup(null);
  };

  const onDelete = () => {
    deleteCandidate(deletePopup.uuid, true).then(() => {
      setDeletePopup(null);
      getTableData();
    });
  };

  useEffect(() => {
    dispatch(
      getListHrManagers({
        role: [ROLES.RECRUITER, ROLES.RECRUITER_LEAD, ROLES.HR_LEAD],
        my_recruiters_group: user.active_role.name === ROLES.RECRUITER_LEAD,
        ordering: 'full_name',
      })
    );
  }, []);

  useEffect(getTableData, [
    tableOptions.sorting,
    tableOptions.limit,
    tableOptions.offset,
    tableOptions.page,
    tableOptions.search,
    tableOptions.period,
  ]);

  return (
    <>
      <Table
        rowKey="uuid"
        className="self-submitted-candidates"
        cardTitle={t('translation:ssc_dashboard.title')}
        stickyHeader
        cardCount={count}
        emptyComponent={
          <Empty hasSearch={tableOptions.search || tableOptions.period?.[0] || tableOptions.period?.[1]} />
        }
        extra={
          <div className="self-submitted-candidates__header-extra">
            <Input type="search" placeholder={t('translation:ssc_dashboard.ph_search')} onDebounceChange={setSearch} />
            <RangePicker value={tableOptions.period} onChange={setPeriod} />
            <Button type={BUTTON_TYPES.PRIMARY} onClick={onOpenConfirmPopup} disabled={!selectedRows.length}>
              {t('translation:ssc_dashboard.button_delete')}
            </Button>
          </div>
        }
        columns={COLUMNS_SSC(tableOptions.sorting, setSorting, onAction)}
        loading={loading}
        data={tableData}
        limit={tableOptions.limit}
        page={tableOptions.page}
        itemsCount={tableOptions.itemsCount}
        updateParams={onChangeFilter}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selectedRows.map(r => r.uuid),
          onChange: (_, rows) => setSelectedRows(rows),
        }}
      />
      <TableMobile
        className="self-submitted-candidates_mobile"
        data={tableData as never}
        columns={COLUMNS_SSC(tableOptions.sorting, setSorting, onAction)}
        empty={<Empty hasSearch={tableOptions.search || tableOptions.period?.[0] || tableOptions.period?.[1]} />}
      />
      <div className="self-submitted-candidates_mobile-footer">
        <TableFooter
          limit={tableOptions.limit}
          page={tableOptions.page}
          onChangePage={value => onChangeFilter('page', value)}
          onChangeLimit={value => onChangeFilter('limit', value)}
          itemsCount={tableOptions.itemsCount}
          loading={loading}
          newLoading
        />
      </div>
      <PopupReassignCandidate
        candidate={assignPopup || {}}
        open={!!assignPopup}
        onAssign={onAssign}
        onClose={onAssignPopupClose}
        isSSC
      />
      <PopupMarkAsReviewed
        open={!!reviewPopup}
        onClose={onReviewPopupClose}
        candidateUuid={reviewPopup?.uuid}
        candidateName={reviewPopup?.full_name_latin}
        onReviewed={getTableData}
      />
      <DeleteCandidatePopup
        {...({
          open: !!deletePopup,
          candidateData: deletePopup,
          onClose: onDeletePopupClose,
          onDelete,
        } as unknown as React.ComponentProps<typeof DeleteCandidatePopup>)}
      />
      <PopupCandidateDuplicate
        open={!!duplicatesPopup}
        candidateDuplicates={potentialDuplicates as never}
        duplicate={duplicatesPopup as never}
        onClose={onDuplicatesPopupClose}
        loading={!!duplicatesPopup && !potentialDuplicates.length}
        isMergeAble
      />
      <Popup
        className="popup-delete-ssc"
        open={openConfirmPopup}
        title={t('translation:ssc_dashboard.title_delete_candidates')}
        cancelButtonProps={{ onClick: onCloseConfirmPopup, children: 'Back to page' }}
        okButtonProps={{ onClick: onBulkDelete, children: 'Delete' }}
      >
        {t('translation:ssc_dashboard.text_confirm_delete', {
          count: selectedRows.length,
          s: selectedRows.length % 10 !== 1 ? 's' : '',
        } as Record<string, unknown>)}
      </Popup>
    </>
  );
};
